import React, { memo, useCallback } from 'react';
import { AudioItem } from '../../items/audio/audio-item-type';
import { VideoItem } from '../../items/video/video-item-type';
import { Slider, ValueInputField } from '@crabs-ai/ds-react';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { MAX_VOLUME_DB, MIN_VOLUME_DB } from '../../utils/decibels';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { InspectorSubLabel } from '../components/inspector-label';
import { useSelectionValues } from '../utils/use-selection-values';
import { useTranslations } from 'next-intl';

const VolumeControlsUnmemoized: React.FC<{
  decibelAdjustment: number;
  itemId: string;
}> = ({ decibelAdjustment, itemId }) => {
  const t = useTranslations();

  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<AudioItem | VideoItem, number>(
    itemId,
    (item) => item.decibelAdjustment,
  );

  const setVolume = useCallback(
    (newDecibelAdjustment: number, commitToUndoStack: boolean) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const prev = i as AudioItem | VideoItem;
            if (prev.decibelAdjustment === newDecibelAdjustment) {
              return prev;
            }
            return {
              ...prev,
              decibelAdjustment: newDecibelAdjustment,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const handleSliderChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      setVolume(value, commitToUndoStack);
    },
    [setVolume],
  );

  return (
    <div>
      {/* <InspectorSubLabel>
        {t('Editor.inspector.volume-control')}
      </InspectorSubLabel> */}
      <div className="flex w-full items-center gap-3">
        <Slider
          value={[decibelAdjustment]}
          onValueChange={(values) => handleSliderChange(values[0] ?? 0, false)}
          onValueCommit={(values) => handleSliderChange(values[0] ?? 0, true)}
          min={MIN_VOLUME_DB}
          max={MAX_VOLUME_DB}
          step={0.5}
          className="flex-1"
          isShowTooltip={false}
          aria-label={
            isMixed
              ? 'Volume: Mixed'
              : `Volume: ${decibelAdjustment.toFixed(1)} db`
          }
        />
        <ValueInputField
          value={Number(decibelAdjustment.toFixed(1))}
          setValue={(update) =>
            handleSliderChange(update.num, update.commitToUndoStack)
          }
          min={MIN_VOLUME_DB}
          max={MAX_VOLUME_DB}
          step={0.5}
          isUnit="dB"
          hasMixedValue={isMixed}
          accessibilityLabel="Volume"
          className="w-[96px]"
        />
      </div>
    </div>
  );
};

export const VolumeControls = memo(VolumeControlsUnmemoized);
